import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const IntegrationStatusPanel = ({ isCollapsed, onToggle }) => {
  const [integrations, setIntegrations] = useState([
    {
      id: 'telephony',
      name: 'Telephony System',
      status: 'connected',
      responseTime: 45,
      lastCheck: new Date(),
      endpoint: 'tel-api.insureconnect.com',
      version: 'v2.1.3'
    },
    {
      id: 'payment',
      name: 'Payment Gateway',
      status: 'connected',
      responseTime: 120,
      lastCheck: new Date(),
      endpoint: 'payments.insureconnect.com',
      version: 'v1.8.2'
    },
    {
      id: 'crm_salesforce',
      name: 'Salesforce CRM',
      status: 'degraded',
      responseTime: 850,
      lastCheck: new Date(),
      endpoint: 'api.salesforce.com',
      version: 'v54.0'
    },
    {
      id: 'crm_hubspot',
      name: 'HubSpot CRM',
      status: 'connected',
      responseTime: 180,
      lastCheck: new Date(),
      endpoint: 'api.hubapi.com',
      version: 'v3'
    },
    {
      id: 'analytics',
      name: 'Analytics Engine',
      status: 'connected',
      responseTime: 95,
      lastCheck: new Date(),
      endpoint: 'analytics.insureconnect.com',
      version: 'v3.2.1'
    },
    {
      id: 'notification',
      name: 'Notification Service',
      status: 'error',
      responseTime: 0,
      lastCheck: new Date(Date.now() - 300000),
      endpoint: 'notify.insureconnect.com',
      version: 'v1.5.0'
    }
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIntegrations(prev => prev?.map(integration => ({
        ...integration,
        responseTime: integration?.status === 'connected' 
          ? Math.max(20, integration?.responseTime + Math.floor(Math.random() * 40 - 20))
          : integration?.status === 'degraded'
          ? Math.max(200, integration?.responseTime + Math.floor(Math.random() * 200 - 100))
          : 0,
        lastCheck: integration?.status !== 'error' ? new Date() : integration?.lastCheck
      })));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-success';
      case 'degraded': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return 'CheckCircle';
      case 'degraded': return 'AlertTriangle';
      case 'error': return 'XCircle';
      default: return 'HelpCircle';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'connected': return 'bg-success/10 text-success';
      case 'degraded': return 'bg-warning/10 text-warning';
      case 'error': return 'bg-error/10 text-error';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getResponseTimeColor = (responseTime) => {
    if (responseTime === 0) return 'text-error';
    if (responseTime > 500) return 'text-error';
    if (responseTime > 200) return 'text-warning';
    return 'text-success';
  };

  const formatLastCheck = (date) => {
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const connectedCount = integrations?.filter(i => i?.status === 'connected')?.length;
  const totalCount = integrations?.length;

  if (isCollapsed) {
    return (
      <div className="fixed bottom-4 left-4 z-1000">
        <button
          onClick={onToggle}
          className="flex items-center space-x-2 px-3 py-2 bg-card border border-border rounded-lg shadow-card hover:shadow-modal transition-smooth"
        >
          <div className="flex items-center space-x-2">
            <div className="flex -space-x-1">
              {integrations?.slice(0, 3)?.map((integration) => (
                <div
                  key={integration?.id}
                  className={`w-3 h-3 rounded-full border border-card ${
                    integration?.status === 'connected' ? 'bg-success' :
                    integration?.status === 'degraded' ? 'bg-warning' : 'bg-error'
                  }`}
                />
              ))}
            </div>
            <span className="text-sm font-medium text-foreground">
              {connectedCount}/{totalCount} Connected
            </span>
          </div>
          <Icon name="ChevronUp" size={16} className="text-muted-foreground" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 w-80 bg-card border border-border rounded-lg shadow-modal z-1000">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Wifi" size={20} className="text-foreground" />
          <h3 className="font-semibold text-foreground">Integration Status</h3>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            connectedCount === totalCount ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
          }`}>
            {connectedCount}/{totalCount} Online
          </span>
          <Button
            variant="ghost"
            size="sm"
            iconName="ChevronDown"
            onClick={onToggle}
          />
        </div>
      </div>
      {/* Integration List */}
      <div className="max-h-64 overflow-y-auto">
        <div className="divide-y divide-border">
          {integrations?.map((integration) => (
            <div key={integration?.id} className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={getStatusIcon(integration?.status)} 
                    size={16} 
                    className={getStatusColor(integration?.status)} 
                  />
                  <span className="text-sm font-medium text-foreground">
                    {integration?.name}
                  </span>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(integration?.status)}`}>
                  {integration?.status}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Response:</span>
                  <span className={`ml-1 font-mono ${getResponseTimeColor(integration?.responseTime)}`}>
                    {integration?.responseTime > 0 ? `${integration?.responseTime}ms` : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Version:</span>
                  <span className="text-foreground ml-1">{integration?.version}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Endpoint:</span>
                  <span className="text-foreground ml-1 font-mono text-xs">
                    {integration?.endpoint}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Last check:</span>
                  <span className="text-foreground ml-1">
                    {formatLastCheck(integration?.lastCheck)}
                  </span>
                </div>
              </div>

              {integration?.status === 'error' && (
                <div className="mt-2 flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RefreshCw"
                    className="text-xs"
                  >
                    Retry
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ExternalLink"
                    className="text-xs"
                  >
                    Logs
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Footer */}
      <div className="p-3 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Auto-refresh: 5s</span>
          <button className="text-accent hover:text-accent/80 transition-smooth">
            View All Logs
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntegrationStatusPanel;